/**
 * App environment config — single source of truth for env var access.
 * Vite replaces import.meta.env.* at build/dev time.
 * In tests, mock this module instead of polyfilling import.meta.
 */

export const IS_DEV = !!import.meta.env.DEV;

export const env = {
  REACT_APP_ENTRA_TENANT_ID: import.meta.env.REACT_APP_ENTRA_TENANT_ID || "",
  REACT_APP_ENTRA_CLIENT_ID: import.meta.env.REACT_APP_ENTRA_CLIENT_ID || "",
  REACT_APP_ENTRA_REDIRECT_URI: import.meta.env.REACT_APP_ENTRA_REDIRECT_URI || "",
  REACT_APP_CATALOG_URL: import.meta.env.REACT_APP_CATALOG_URL || "",
  // Adoption artifact (Contract 7), joined onto the catalog at read time. Empty is
  // the normal case: fetchAdoption then derives adoption.json as a sibling of the
  // catalog URL, which is where the refresh job publishes it. Set this to point
  // local dev at the committed fixture: REACT_APP_ADOPTION_URL=/mock-adoption.json
  REACT_APP_ADOPTION_URL: import.meta.env.REACT_APP_ADOPTION_URL || "",
  // Community vote tallies (Contract 8). Empty is the normal case: the client then
  // reads them from the feedback API's /feedback/tallies endpoint. Set this to point
  // local dev at the committed fixture instead — REACT_APP_FEEDBACK_URL=/mock-feedback.json
  // — which is the only way to see tallies without a deployed backend.
  //
  // Read path only. Casting a vote always goes to the API: the tally is computed
  // server-side from the voter's prior state, so there is deliberately no fixture
  // equivalent for writes (see contract-8).
  REACT_APP_FEEDBACK_URL: import.meta.env.REACT_APP_FEEDBACK_URL || "",
  REACT_APP_MARKETPLACE_NAME: import.meta.env.REACT_APP_MARKETPLACE_NAME || "skills-marketplace-catalog",
  REACT_APP_CATALOG_GIT_URL: import.meta.env.REACT_APP_CATALOG_GIT_URL || "https://ghe.coxautoinc.com/CoxAuto/skills-marketplace-catalog.git",
  // Identity of the contributed-skills write-path repo (issue #404). Used by
  // isContributedRecord (lib/contributed.ts) to gate Edit/Delete affordances to
  // records that actually live in this repo. Ships a hardcoded prod default (like
  // REACT_APP_CATALOG_GIT_URL) so an empty build var can't fail closed and make
  // Edit/Delete silently vanish for legitimate contributors.
  //
  // NOTE: this default is the PROD owner (CoxAuto). Non-prod's contributed-skills repo
  // is owned by Delivery-Platform, so a non-prod/ephemeral build MUST set this var
  // explicitly (the deploy scripts do, from the git_publisher_repo_owner tfvar). If a
  // non-prod build ever dropped to this default, it would parse cleanly to the WRONG
  // owner and Edit/Delete would fail closed for every non-prod record — WITHOUT tripping
  // warnMisconfiguredOnce, which only catches an empty/unparseable URL, not valid-but-wrong.
  REACT_APP_CONTRIBUTED_SKILLS_REPO_URL: import.meta.env.REACT_APP_CONTRIBUTED_SKILLS_REPO_URL || "https://ghe.coxautoinc.com/CoxAuto/contributed-skills.git",
  REACT_APP_GITHUB_PROXY_URL: import.meta.env.REACT_APP_GITHUB_PROXY_URL || "",
  REACT_APP_NEWRELIC_ACCOUNT_ID: import.meta.env.REACT_APP_NEWRELIC_ACCOUNT_ID || "",
  REACT_APP_NEWRELIC_APPLICATION_ID: import.meta.env.REACT_APP_NEWRELIC_APPLICATION_ID || "",
  REACT_APP_NEWRELIC_LICENSE_KEY: import.meta.env.REACT_APP_NEWRELIC_LICENSE_KEY || "",
  REACT_APP_NEWRELIC_TRUST_KEY: import.meta.env.REACT_APP_NEWRELIC_TRUST_KEY || "",
  REACT_APP_NEWRELIC_CI_ID: import.meta.env.REACT_APP_NEWRELIC_CI_ID || "",
  REACT_APP_GTM_CONTAINER_ID: import.meta.env.REACT_APP_GTM_CONTAINER_ID || "",
  /**
   * Gates the AI-mode UI (the "Ask AI" affordance and the semantic-fusion fetch). Off unless
   * explicitly "true" — so an environment whose `/api` backend isn't wired never shows a
   * non-functional "Ask AI" button. deploy-web-full.sh currently leaves it empty for every env
   * (the feature is paused); flip it back to "true" there once AI search is deliberately turned
   * back on (product-management#804). This is purely the UI gate; the backend gate is the
   * `enable_ai_search` Terraform flag.
   */
  REACT_APP_AI_SEARCH_ENABLED: import.meta.env.REACT_APP_AI_SEARCH_ENABLED || "",
};
